import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@fmc-digital/i18n";
import { Button, cn, ErrorBanner, TextField } from "@fmc-digital/ui";
import { FeedbackIcon } from "@/pages/register/icons/FeedbackIcon";
import {
  submitRegistrationFeedback,
  submitRegistrationFeedbackComment,
} from "@/pages/register/feedback/submitRegistrationFeedback";
import type { RegistrationFeedbackMetadata } from "@/pages/register/feedback/registrationFeedback.types";
import {
  getNpsRatingDisplayColor,
  getRegisterFeedbackExitNavigation,
  getRegisterFeedbackThankYouNavigation,
  isFeedbackCommentEligibleRating,
  type RegisterFeedbackLocationState,
} from "@/pages/register/feedback/register-feedback.navigation";
import { getNpsRatingBucket } from "@/components/molecules/NpsRatingScale/nps-rating-scale.styles";
import {
  registrationFeedbackMaybeLaterStyles,
  registrationFeedbackNpsErrorBannerStyles,
  registrationFeedbackNpsErrorBannerWrapperStyles,
} from "@/components/organisms/RegistrationFeedbackNps/registration-feedback-nps.styles";
import {
  registrationFeedbackCommentActionsStyles,
  registrationFeedbackCommentBoxStyles,
  registrationFeedbackCommentCopyStyles,
  registrationFeedbackCommentCounterStyles,
  registrationFeedbackCommentDesktopCopyStyles,
  registrationFeedbackCommentFieldSectionStyles,
  registrationFeedbackCommentFieldWrapperStyles,
  registrationFeedbackCommentHeaderStyles,
  registrationFeedbackCommentIconStyles,
  registrationFeedbackCommentIntroStyles,
  registrationFeedbackCommentPromptAfterStyles,
  registrationFeedbackCommentPromptBeforeStyles,
  registrationFeedbackCommentPromptStyles,
  registrationFeedbackCommentRatingStyles,
  registrationFeedbackCommentRootStyles,
  registrationFeedbackCommentTitleStyles,
} from "./registration-feedback-comment.styles";

export const FEEDBACK_COMMENT_MAX_LENGTH = 240;

const EMPTY_FEEDBACK_METADATA: RegistrationFeedbackMetadata = {
  email: "",
  userUUID: "",
  loanNumber: "",
};

export interface RegistrationFeedbackCommentProps {
  username?: string;
  rating: number;
  feedbackContext?: RegistrationFeedbackMetadata;
  feedbackState?: RegisterFeedbackLocationState;
}

export function RegistrationFeedbackComment({
  username,
  rating,
  feedbackContext,
  feedbackState,
}: Readonly<RegistrationFeedbackCommentProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const metadata = feedbackContext ?? EMPTY_FEEDBACK_METADATA;
  const bucket = getNpsRatingBucket(rating);
  const ratingColor = getNpsRatingDisplayColor(rating);
  const canSend = !loading && comment.trim().length > 0;

  const promptBeforeKey =
    bucket === "promoter"
      ? "auth.registration.feedback.comment.promptPromoterBefore"
      : "auth.registration.feedback.comment.promptDetractorBefore";
  const promptAfterKey =
    bucket === "promoter"
      ? "auth.registration.feedback.comment.promptPromoterAfter"
      : "auth.registration.feedback.comment.promptDetractorAfter";

  const handleMaybeLater = () => {
    const { route, state } = getRegisterFeedbackExitNavigation(username);
    navigate(route, state ? { state } : undefined);
  };

  const handleSkipComment = async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    setSubmitError(null);
    try {
      if (isFeedbackCommentEligibleRating(rating)) {
        await submitRegistrationFeedback(metadata, rating);
        const { route, state } = getRegisterFeedbackThankYouNavigation(feedbackState, rating);
        navigate(route, state ? { state } : undefined);
        return;
      }

      const { route, state } = getRegisterFeedbackExitNavigation(username);
      navigate(route, state ? { state } : undefined);
    } catch (error) {
      console.error("[Registration Feedback Comment] Skip submission failed", error);
      setSubmitError(t("auth.registration.feedback.comment.submitError"));
    } finally {
      setLoading(false);
    }
  };

  const handleSendFeedback = async () => {
    if (!canSend) {
      return;
    }

    setLoading(true);
    setSubmitError(null);
    try {
      await submitRegistrationFeedbackComment(metadata, rating, comment.trim());
      const { route, state } = getRegisterFeedbackThankYouNavigation(feedbackState, rating);
      navigate(route, state ? { state } : undefined);
    } catch (error) {
      console.error("[Registration Feedback Comment] Submission failed", error);
      setSubmitError(t("auth.registration.feedback.comment.submitError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cn(registrationFeedbackCommentRootStyles())} data-testid="registration-feedback-comment">
      <div className={cn(registrationFeedbackCommentHeaderStyles())}>
        <FeedbackIcon className={cn(registrationFeedbackCommentIconStyles())} />
        <h1 className={cn(registrationFeedbackCommentTitleStyles())}>
          {t("auth.registration.feedback.comment.title")}
        </h1>
        <div
          className={cn(registrationFeedbackCommentCopyStyles())}
          data-testid="feedback-comment-copy"
        >
          <p className={cn(registrationFeedbackCommentIntroStyles())}>
            {t("auth.registration.feedback.comment.intro")}
          </p>
          <p className={cn(registrationFeedbackCommentPromptStyles())}>
            <span className={cn(registrationFeedbackCommentPromptBeforeStyles())}>
              {t(promptBeforeKey)}
            </span>
            <span
              className={cn(registrationFeedbackCommentRatingStyles())}
              style={{ color: ratingColor }}
              data-testid="feedback-comment-rating"
            >
              {rating}
            </span>
            <span className={cn(registrationFeedbackCommentPromptAfterStyles())}>
              {t(promptAfterKey)}
            </span>
          </p>
        </div>
        <p
          className={cn(registrationFeedbackCommentDesktopCopyStyles())}
          data-testid="feedback-comment-copy-desktop"
        >
          {t("auth.registration.feedback.comment.intro")}{" "}
          {t(promptBeforeKey)}
          <span style={{ color: ratingColor }}>{rating}</span>
          {t(promptAfterKey)}
        </p>
      </div>

      <div className={cn(registrationFeedbackCommentFieldSectionStyles())}>
        <div
          className={cn(registrationFeedbackCommentBoxStyles())}
          data-testid="feedback-comment-box"
        >
          <TextField
            type="textarea"
            className={cn(registrationFeedbackCommentFieldWrapperStyles())}
            placeholder={t("auth.registration.feedback.comment.placeholder")}
            value={comment}
            onChange={(event) => {
              setComment(event.target.value);
              setSubmitError(null);
            }}
            fullWidth
            rows={4}
            maxLength={FEEDBACK_COMMENT_MAX_LENGTH}
            showCounter
            aria-label={t("auth.registration.feedback.comment.fieldAriaLabel")}
            testID="feedback-comment-textarea"
          />
        </div>
        <p
          className={cn(registrationFeedbackCommentCounterStyles())}
          data-testid="feedback-comment-char-counter-desktop"
          aria-live="polite"
        >
          {comment.length} / {FEEDBACK_COMMENT_MAX_LENGTH}
        </p>
      </div>

      <div className={cn(registrationFeedbackCommentActionsStyles())}>
        <Button
          variant="filled"
          fullWidth
          onClick={handleSendFeedback}
          disabled={!canSend}
          data-testid="feedback-comment-send-button"
          aria-label={t("auth.registration.feedback.comment.sendFeedbackAriaLabel")}
        >
          {loading
            ? t("auth.registration.feedback.comment.submitting")
            : t("auth.registration.feedback.comment.sendFeedback")}
        </Button>
        <button
          type="button"
          className={cn(registrationFeedbackMaybeLaterStyles())}
          onClick={submitError ? handleMaybeLater : handleSkipComment}
          disabled={loading}
          data-testid={
            submitError ? "feedback-comment-maybe-later-button" : "feedback-comment-skip-button"
          }
          aria-label={
            submitError
              ? t("auth.registration.feedback.comment.maybeLaterAriaLabel")
              : t("auth.registration.feedback.comment.skipCommentAriaLabel")
          }
        >
          {submitError
            ? t("auth.registration.feedback.comment.maybeLater")
            : t("auth.registration.feedback.comment.skipComment")}
        </button>
      </div>

      {submitError ? (
        <div className={cn(registrationFeedbackNpsErrorBannerWrapperStyles())}>
          <ErrorBanner
            message={submitError}
            className={cn(registrationFeedbackNpsErrorBannerStyles())}
            testID="feedback-comment-error-banner"
          />
        </div>
      ) : null}
    </div>
  );
}
